// src/belediye/hooks/useYeniOzellikIpucu.js
//
// "YENİ" tanıtım balonu — yeni açılan bir yeteneğin fark edilmesi için.
//
// NEDEN SAYIYLA, GÜNLE DEĞİL:
// Gün sayacı, izni bugün açılan kişiyle üç ay sonra açılan kişiye farklı
// davranır: geç gelen kullanıcı balonu hiç görmeyebilir. Sayaç ise kişiye
// bağlıdır — izin ne zaman verilirse verilsin, o kişi ekrana ilk N kez
// girdiğinde balonu görür.
//
// SAYAÇ NE ZAMAN ARTAR: yalnız balon GERÇEKTEN GÖSTERİLDİĞİNDE. Düğme izin
// yokken çizilmiyorsa ya da sayfa henüz yüklenmediyse hak yanmaz; aksi hâlde
// kullanıcı hiç görmediği bir ipucunun hakkını tüketmiş olurdu.
//
// SAKLAMA YERİ localStorage:
// Sunucuda tutmak ayrı bir tablo + uç + yazma isteği demek; taşıdığı bilgi
// ("bu ipucunu gördü") kaybolursa en kötü sonuç balonun bir kez daha
// çıkmasıdır. Bu bedel, kalıcı depolama maliyetini haklı çıkarmıyor.
// Anahtar KULLANICI KİMLİĞİYLE ayrılır: ortak bilgisayarda bir personelin
// gördüğü ipucu diğerinden gizlenmemeli.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const ONEK = "kim.yeniOzellik";
const VARSAYILAN_ADET = 3;
// Balon kendiliğinden kaybolur: içinde okunacak bir metin yok, yalnız
// "burada yeni bir şey var" diyor. Kapatma düğmesi koymak, iki kelimelik
// bir işaret için kullanıcıdan eylem istemek olurdu.
const VARSAYILAN_SURE_MS = 5000;

function anahtarUret(kullaniciId, ad) {
  return `${ONEK}.${kullaniciId ?? "misafir"}.${ad}`;
}

function oku(anahtar) {
  try {
    const ham = window.localStorage.getItem(anahtar);
    if (!ham) return { adet: 0, bitti: false };
    const v = JSON.parse(ham);
    return { adet: Number(v?.adet) || 0, bitti: v?.bitti === true };
  } catch {
    // Bozuk/erişilemez depolama ipucunu engellememeli: sıfırdan başla.
    return { adet: 0, bitti: false };
  }
}

function yaz(anahtar, deger) {
  try {
    window.localStorage.setItem(anahtar, JSON.stringify(deger));
  } catch {
    /* gizli sekme / kota dolu — ipucu bu oturumda çalışır, kalıcı olmaz */
  }
}

/**
 * @param {string} ad        Özelliğin sabit adı (ör. "resmiBelgeYazdir")
 * @param {object} secenek
 * @param {boolean} secenek.aktif   Balon gösterilebilir mi (izin + düğme çizili mi)
 * @param {number}  secenek.adet    Kaç kez gösterilecek (varsayılan 3)
 * @param {number}  secenek.sureMs  Kaç ms sonra kendiliğinden kaybolsun (0 = kalıcı)
 * @returns {{gorunur: boolean, kapat: (kalici?: boolean) => void}}
 */
export default function useYeniOzellikIpucu(
  ad,
  { aktif = true, adet = VARSAYILAN_ADET, sureMs = VARSAYILAN_SURE_MS } = {}
) {
  const kullaniciId = useSelector((s) => s?.belediyeAuth?.user?.id ?? null);
  const [gorunur, setGorunur] = useState(false);
  // Aynı montajda iki kez sayılmasın (React 18 StrictMode effect'i iki kez çalıştırır).
  const sayildi = useRef(false);

  useEffect(() => {
    if (!aktif || sayildi.current) return;

    const anahtar = anahtarUret(kullaniciId, ad);
    const durum = oku(anahtar);
    if (durum.bitti || durum.adet >= adet) return;

    sayildi.current = true;
    yaz(anahtar, { adet: durum.adet + 1, bitti: false });
    setGorunur(true);
  }, [aktif, ad, adet, kullaniciId]);

  // Kendiliğinden kaybolma. Sayaç zaten harcandı; bu yalnız EKRANDAN
  // kaldırır, kalan hakları etkilemez.
  useEffect(() => {
    if (!gorunur || !sureMs) return undefined;
    const zaman = setTimeout(() => setGorunur(false), sureMs);
    return () => clearTimeout(zaman);
  }, [gorunur, sureMs]);

  const kapat = useCallback(
    (kalici = false) => {
      setGorunur(false);
      if (!kalici) return;
      // "Anladım" ya da özelliği kullanma → bir daha hiç gösterme.
      yaz(anahtarUret(kullaniciId, ad), { adet, bitti: true });
    },
    [ad, adet, kullaniciId]
  );

  return { gorunur, kapat };
}
